import Phaser from 'phaser';
import { TEXTURE_KEYS } from './assetKeys';
import { PALETTE } from './palette';

type ProgressCallback = (progress: number) => void;

export class AssetGenerator {
  static readonly TOTAL_STEPS = 36;

  static generateAll(scene: Phaser.Scene, onProgress?: ProgressCallback): void {
    let step = 0;
    const tick = () => {
      step++;
      onProgress?.(step / AssetGenerator.TOTAL_STEPS);
    };

    AssetGenerator.generatePlayerFrames(scene, tick);
    AssetGenerator.generateNPCs(scene, tick);
    AssetGenerator.generateEnvironment(scene, tick);
    AssetGenerator.generateBackgrounds(scene, tick);
    AssetGenerator.generateUI(scene, tick);
  }

  private static makeGraphics(scene: Phaser.Scene): Phaser.GameObjects.Graphics {
    return scene.make.graphics({ x: 0, y: 0 });
  }

  private static commit(
    scene: Phaser.Scene,
    g: Phaser.GameObjects.Graphics,
    key: string,
    w: number,
    h: number,
  ): void {
    if (scene.textures.exists(key)) {
      scene.textures.remove(key);
    }
    g.generateTexture(key, w, h);
    g.destroy();
  }

  // ─── Player ───────────────────────────────────────────────

  private static generatePlayerFrames(scene: Phaser.Scene, tick: () => void): void {
    AssetGenerator.drawBujangFrame(scene, TEXTURE_KEYS.PLAYER_IDLE_0, 0);
    tick();
    AssetGenerator.drawBujangFrame(scene, TEXTURE_KEYS.PLAYER_IDLE_1, 1);
    tick();
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawBujangFrame(scene, `player_walk_${i}`, 2 + i);
      tick();
    }
  }

  private static drawBujangFrame(scene: Phaser.Scene, key: string, frame: number): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 64;
    const H = 80;
    const cx = W / 2;
    const groundY = H - 2;

    const isWalk = frame >= 2;
    const walkPhase = isWalk ? frame - 2 : 0;
    const idleBreath = !isWalk && frame === 1;

    const walkPoses = [
      { leftX: -11, rightX: 5, leftH: 16, rightH: 12, leftY: 0, rightY: 4, armL: 4, armR: -4 },
      { leftX: -7, rightX: 1, leftH: 14, rightH: 14, leftY: 2, rightY: 2, armL: 0, armR: 0 },
      { leftX: 5, rightX: -11, leftH: 12, rightH: 16, leftY: 4, rightY: 0, armL: -4, armR: 4 },
      { leftX: 1, rightX: -7, leftH: 14, rightH: 14, leftY: 2, rightY: 2, armL: 0, armR: 0 },
    ];
    const pose = isWalk ? walkPoses[walkPhase] : null;

    const torsoShift = idleBreath ? -1 : 0;

    // Bayangan — selalu di tanah
    g.fillStyle(0x000000, 0.22);
    g.fillEllipse(cx, groundY + 1, 30, 7);

    const drawLeg = (x: number, height: number, lift: number) => {
      g.fillStyle(PALETTE.woodDark);
      g.fillRoundedRect(x, groundY - height - lift, 8, height, 3);
      g.fillStyle(PALETTE.woodMid, 0.55);
      g.fillRoundedRect(x + 1, groundY - height - lift + 2, 6, height - 4, 2);
    };

    if (isWalk && pose) {
      drawLeg(cx + pose.rightX, pose.rightH, pose.rightY);
    } else {
      drawLeg(cx + 2, 16, 0);
      drawLeg(cx - 10, 16, 0);
    }

    const bodyTop = groundY - 42 + torsoShift;
    g.fillStyle(PALETTE.clothGreen);
    g.fillRoundedRect(cx - 14, bodyTop, 28, 30, 6);
    g.fillStyle(PALETTE.clothBlue, 0.4);
    g.fillTriangle(cx - 14, bodyTop + 2, cx + 14, bodyTop + 24, cx - 2, bodyTop + 24);
    g.fillStyle(PALETTE.forestLight, 0.45);
    g.fillRoundedRect(cx - 12, bodyTop + 2, 24, 10, 4);
    g.fillStyle(PALETTE.gold);
    g.fillRect(cx - 14, bodyTop + 12, 28, 5);
    g.fillStyle(PALETTE.clothRed);
    g.fillTriangle(cx - 14, bodyTop, cx + 14, bodyTop, cx, bodyTop + 14);
    g.fillStyle(PALETTE.gold, 0.8);
    g.fillCircle(cx, bodyTop + 14, 2);
    g.fillCircle(cx, bodyTop + 20, 2);
    g.fillStyle(PALETTE.woodDark, 0.9);
    g.fillRoundedRect(cx + 10, bodyTop + 10, 4, 18, 2);
    g.fillStyle(PALETTE.gold, 0.85);
    g.fillRect(cx + 9, bodyTop + 14, 6, 3);

    g.lineStyle(1.5, PALETTE.outline, 0.55);
    g.strokeRoundedRect(cx - 14, bodyTop, 28, 30, 6);

    const armL = isWalk && pose ? pose.armL : 0;
    const armR = isWalk && pose ? pose.armR : 0;
    g.fillStyle(PALETTE.skin);
    g.fillRoundedRect(cx - 20, bodyTop + 4 + armL, 8, 18, 4);
    g.fillRoundedRect(cx + 12, bodyTop + 4 + armR, 8, 18, 4);

    if (isWalk && pose) {
      drawLeg(cx + pose.leftX, pose.leftH, pose.leftY);
    }

    const headY = bodyTop - 10;
    g.fillStyle(PALETTE.skin);
    g.fillCircle(cx, headY, 12);
    g.fillStyle(PALETTE.skinShadow);
    g.fillCircle(cx - 3, headY + 2, 2);
    g.fillStyle(PALETTE.hairDark);
    g.fillEllipse(cx, headY - 8, 20, 10);

    g.fillStyle(PALETTE.clothRed);
    g.fillRect(cx - 13, headY - 10, 26, 6);
    g.fillTriangle(cx - 13, headY - 4, cx - 18, headY + 4, cx - 8, headY - 4);
    g.fillTriangle(cx + 13, headY - 4, cx + 18, headY + 4, cx + 8, headY - 4);

    g.fillStyle(PALETTE.black);
    g.fillCircle(cx - 4, headY - 1, 2);
    g.fillCircle(cx + 4, headY - 1, 2);
    g.fillRect(cx - 5, headY + 1, 10, 1);
    g.lineStyle(2, PALETTE.black, 0.6);
    g.beginPath();
    g.arc(cx, headY + 4, 5, 0.2, Math.PI - 0.2);
    g.strokePath();

    AssetGenerator.commit(scene, g, key, W, H);
  }

  // ─── NPCs ─────────────────────────────────────────────────

  private static generateNPCs(scene: Phaser.Scene, tick: () => void): void {
    AssetGenerator.drawKakekSakti(scene);
    tick();
    AssetGenerator.drawIbundo(scene);
    tick();
    AssetGenerator.drawLansia(scene);
    tick();
    AssetGenerator.drawIbuHamil(scene);
    tick();
    AssetGenerator.drawWarga(scene);
    tick();
    AssetGenerator.drawKera(scene);
    tick();
    AssetGenerator.drawHarimau(scene);
    tick();
  }

  private static drawKakekSakti(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 64;
    const H = 88;
    const cx = W / 2;
    const by = H - 6;

    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(cx, by + 2, 26, 8);

    // Tongkat
    g.fillStyle(PALETTE.woodDark);
    g.fillRoundedRect(cx + 18, by - 70, 5, 72, 2);
    g.fillStyle(PALETTE.gold);
    g.fillCircle(cx + 20, by - 72, 5);

    g.fillStyle(PALETTE.woodMid);
    g.fillRoundedRect(cx - 8, by - 14, 7, 14, 2);
    g.fillRoundedRect(cx + 1, by - 14, 7, 14, 2);

    g.fillStyle(0x325c42);
    g.fillRoundedRect(cx - 16, by - 44, 32, 32, 6);
    g.fillStyle(PALETTE.gold);
    g.fillRect(cx - 16, by - 36, 32, 5);
    g.fillStyle(PALETTE.clothRed, 0.9);
    g.fillTriangle(cx - 10, by - 12, cx, by - 28, cx + 10, by - 12);

    g.fillStyle(PALETTE.skin);
    g.fillCircle(cx, by - 54, 11);
    g.fillStyle(PALETTE.clothBlue, 0.34);
    g.fillTriangle(cx - 16, by - 28, cx + 16, by - 28, cx, by - 8);

    // Jenggot & rambut putih
    g.fillStyle(PALETTE.white);
    g.fillEllipse(cx, by - 48, 14, 10);
    g.fillRect(cx - 12, by - 64, 24, 8);
    g.fillTriangle(cx - 12, by - 56, cx - 16, by - 48, cx - 8, by - 56);
    g.fillStyle(PALETTE.gold, 0.85);
    g.fillCircle(cx + 20, by - 72, 3);

    g.fillStyle(PALETTE.black);
    g.fillCircle(cx - 4, by - 55, 1.5);
    g.fillCircle(cx + 4, by - 55, 1.5);
    g.fillRect(cx - 4, by - 51, 8, 1);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.KAKEK_SAKTI, W, H);
  }

  private static drawIbundo(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 60;
    const H = 84;
    const cx = W / 2;
    const by = H - 6;

    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(cx, by + 2, 24, 7);

    g.fillStyle(PALETTE.clothWarm);
    g.fillRoundedRect(cx - 7, by - 12, 6, 12, 2);
    g.fillRoundedRect(cx + 1, by - 12, 6, 12, 2);

    g.fillStyle(PALETTE.clothWarm);
    g.fillRoundedRect(cx - 18, by - 46, 36, 36, 8);
    g.fillStyle(PALETTE.gold);
    g.fillRect(cx - 18, by - 38, 36, 4);
    g.fillStyle(PALETTE.rafflesiaLight);
    g.fillTriangle(cx, by - 46, cx - 8, by - 34, cx + 8, by - 34);
    g.fillStyle(PALETTE.gold, 0.85);
    g.fillCircle(cx, by - 66, 5);
    g.fillRect(cx - 11, by - 64, 22, 3);

    g.fillStyle(PALETTE.skin);
    g.fillCircle(cx, by - 56, 10);
    g.fillStyle(PALETTE.hairDark);
    g.fillEllipse(cx, by - 62, 16, 10);
    g.fillStyle(PALETTE.clothRed);
    g.fillRect(cx - 14, by - 58, 28, 4);
    g.fillStyle(PALETTE.gold, 0.8);
    g.fillTriangle(cx, by - 72, cx - 4, by - 64, cx + 4, by - 64);

    g.fillStyle(PALETTE.black);
    g.fillCircle(cx - 3, by - 57, 1.5);
    g.fillCircle(cx + 3, by - 57, 1.5);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.IBUNDO, W, H);
  }

  private static drawLansia(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 56;
    const H = 80;
    const cx = W / 2;
    const by = H - 6;

    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(cx, by + 2, 22, 7);

    g.lineStyle(3, PALETTE.woodDark);
    g.lineBetween(cx + 14, by - 10, cx + 14, by - 50);

    g.fillStyle(PALETTE.woodMid);
    g.fillRoundedRect(cx - 7, by - 12, 6, 12, 2);
    g.fillRoundedRect(cx + 1, by - 12, 6, 12, 2);
    g.fillStyle(0x6d6151);
    g.fillRoundedRect(cx - 14, by - 40, 28, 30, 5);
    g.fillStyle(PALETTE.gold, 0.75);
    g.fillRect(cx - 12, by - 33, 24, 3);

    g.fillStyle(PALETTE.skinShadow);
    g.fillCircle(cx, by - 50, 9);
    g.fillStyle(PALETTE.white);
    g.fillEllipse(cx, by - 44, 10, 6);
    g.fillRect(cx - 10, by - 58, 20, 6);
    g.fillStyle(PALETTE.white, 0.8);
    g.fillRect(cx - 12, by - 32, 24, 2);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.LANSIA, W, H);
  }

  private static drawIbuHamil(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 58;
    const H = 82;
    const cx = W / 2;
    const by = H - 6;

    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(cx, by + 2, 24, 7);

    g.fillStyle(PALETTE.clothPurple);
    g.fillRoundedRect(cx - 7, by - 12, 6, 12, 2);
    g.fillRoundedRect(cx + 1, by - 12, 6, 12, 2);
    g.fillStyle(PALETTE.clothPurple);
    g.fillRoundedRect(cx - 16, by - 38, 32, 28, 6);
    g.fillStyle(PALETTE.clothWarm, 0.9);
    g.fillEllipse(cx + 4, by - 28, 14, 16);
    g.fillStyle(PALETTE.gold, 0.75);
    g.fillRect(cx - 14, by - 30, 28, 3);

    g.fillStyle(PALETTE.skin);
    g.fillCircle(cx, by - 50, 9);
    g.fillStyle(PALETTE.hairDark);
    g.fillEllipse(cx, by - 56, 14, 8);
    g.fillStyle(PALETTE.gold, 0.65);
    g.fillRect(cx - 10, by - 20, 20, 2);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.IBU_HAMIL, W, H);
  }

  private static drawWarga(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 52;
    const H = 76;
    const cx = W / 2;
    const by = H - 6;

    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(cx, by + 2, 20, 6);

    g.fillStyle(PALETTE.woodDark);
    g.fillRoundedRect(cx - 6, by - 12, 5, 12, 2);
    g.fillRoundedRect(cx + 1, by - 12, 5, 12, 2);
    g.fillStyle(PALETTE.clothPurple, 0.82);
    g.fillRoundedRect(cx - 12, by - 36, 24, 26, 5);
    g.fillStyle(PALETTE.gold, 0.7);
    g.fillRect(cx - 10, by - 28, 20, 3);

    g.fillStyle(PALETTE.skin);
    g.fillCircle(cx, by - 46, 8);
    g.fillStyle(PALETTE.hairDark);
    g.fillRect(cx - 8, by - 54, 16, 5);
    g.fillStyle(PALETTE.clothWarm, 0.8);
    g.fillRect(cx - 8, by - 18, 16, 3);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.WARGA, W, H);
  }

  private static drawKera(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 72;
    const H = 56;
    const cx = W / 2;
    const by = H - 5;

    g.fillStyle(0x000000, 0.15);
    g.fillEllipse(cx, by + 2, 40, 8);

    g.fillStyle(0x6e4931);
    g.fillEllipse(cx - 2, by - 20, 28, 18);
    g.fillEllipse(cx - 14, by - 16, 18, 14);
    g.fillStyle(0x8a5b3c);
    g.fillEllipse(cx + 10, by - 22, 18, 14);
    g.fillStyle(0x6e4931);
    g.fillEllipse(cx + 18, by - 28, 18, 16);
    g.fillCircle(cx + 23, by - 35, 5);
    g.fillCircle(cx + 13, by - 37, 4);
    g.fillStyle(PALETTE.skinShadow);
    g.fillEllipse(cx + 18, by - 28, 11, 8);
    g.fillEllipse(cx + 8, by - 21, 6, 5);
    g.fillStyle(0x4c301f);
    g.fillRoundedRect(cx - 10, by - 10, 5, 12, 2);
    g.fillRoundedRect(cx + 2, by - 10, 5, 12, 2);
    g.fillRoundedRect(cx + 14, by - 11, 5, 13, 2);
    g.fillRoundedRect(cx - 20, by - 7, 5, 10, 2);
    g.lineStyle(3, 0x4c301f, 1);
    g.beginPath();
    g.arc(cx - 16, by - 28, 20, 2.2, 5.3);
    g.strokePath();
    g.fillStyle(PALETTE.black);
    g.fillCircle(cx + 22, by - 31, 1.4);
    g.fillCircle(cx + 17, by - 30, 1.4);
    g.lineStyle(1, PALETTE.black, 0.6);
    g.lineBetween(cx + 20, by - 25, cx + 25, by - 23);
    g.lineStyle(1, 0xc7986a, 0.45);
    g.lineBetween(cx - 4, by - 20, cx + 6, by - 18);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.KERA, W, H);
  }

  private static drawHarimau(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 96;
    const H = 64;
    const cx = W / 2;
    const by = H - 6;

    g.fillStyle(0x000000, 0.15);
    g.fillEllipse(cx, by + 2, 56, 9);

    g.fillStyle(PALETTE.tigerOrange);
    g.fillEllipse(cx - 2, by - 24, 42, 20);
    g.fillEllipse(cx - 18, by - 20, 24, 18);
    g.fillEllipse(cx + 16, by - 24, 28, 18);
    g.fillEllipse(cx + 30, by - 31, 20, 16);
    g.fillStyle(PALETTE.white, 0.82);
    g.fillEllipse(cx + 31, by - 27, 12, 9);
    g.fillEllipse(cx + 6, by - 18, 20, 8);
    g.fillStyle(0x5b3623);
    g.fillRoundedRect(cx - 20, by - 12, 6, 15, 2);
    g.fillRoundedRect(cx - 6, by - 13, 6, 16, 2);
    g.fillRoundedRect(cx + 12, by - 13, 6, 16, 2);
    g.fillRoundedRect(cx + 24, by - 14, 6, 17, 2);
    g.lineStyle(3, PALETTE.tigerStripe, 1);
    g.lineBetween(cx - 24, by - 23, cx - 39, by - 14);
    g.lineBetween(cx - 24, by - 20, cx - 41, by - 6);
    g.lineBetween(cx - 8, by - 33, cx - 8, by - 14);
    g.lineBetween(cx + 8, by - 34, cx + 8, by - 15);
    g.lineBetween(cx + 22, by - 34, cx + 20, by - 16);
    g.fillStyle(PALETTE.tigerStripe);
    g.fillTriangle(cx + 24, by - 39, cx + 28, by - 32, cx + 31, by - 38);
    g.fillTriangle(cx + 34, by - 40, cx + 37, by - 33, cx + 41, by - 38);
    g.fillStyle(PALETTE.black);
    g.fillCircle(cx + 28, by - 33, 1.5);
    g.fillCircle(cx + 34, by - 33, 1.5);
    g.fillRect(cx + 31, by - 28, 2, 2);
    g.lineStyle(1, PALETTE.black, 0.7);
    g.lineBetween(cx + 31, by - 27, cx + 38, by - 24);
    g.lineBetween(cx + 31, by - 27, cx + 38, by - 20);
    g.lineStyle(1, 0xffffff, 0.18);
    g.lineBetween(cx - 2, by - 27, cx + 14, by - 24);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.HARIMAU, W, H);
  }

  // ─── Environment ──────────────────────────────────────────

  private static generateEnvironment(scene: Phaser.Scene, tick: () => void): void {
    AssetGenerator.drawTreeLarge(scene);
    tick();
    AssetGenerator.drawTreeFallen(scene);
    tick();
    AssetGenerator.drawGroundCrack(scene);
    tick();
    AssetGenerator.drawRafflesia(scene);
    tick();
    AssetGenerator.drawKeris(scene, TEXTURE_KEYS.KERIS_1, PALETTE.kerisBlade, PALETTE.gold);
    tick();
    AssetGenerator.drawKeris(scene, TEXTURE_KEYS.KERIS_2, 0x7eb8da, PALETTE.woodLight);
    tick();
    AssetGenerator.drawKeris(scene, TEXTURE_KEYS.KERIS_3, 0xc9ada7, PALETTE.clothRed);
    tick();
    AssetGenerator.drawHouseBubungan(scene);
    tick();
    AssetGenerator.drawSignBoard(scene);
    tick();
    AssetGenerator.drawPathRiver(scene);
    tick();
    AssetGenerator.drawPathHill(scene);
    tick();
    AssetGenerator.drawSafeGathering(scene);
    tick();
  }

  private static drawTreeLarge(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 100;
    const H = 140;
    const cx = W / 2;

    g.fillStyle(PALETTE.woodDark);
    g.fillRoundedRect(cx - 12, H - 60, 24, 60, 4);
    g.fillStyle(PALETTE.woodMid, 0.45);
    g.fillRect(cx - 3, H - 58, 6, 56);
    g.fillStyle(PALETTE.forestMid);
    g.fillCircle(cx - 30, H - 80, 32);
    g.fillCircle(cx + 28, H - 85, 30);
    g.fillCircle(cx, H - 100, 36);
    g.fillCircle(cx - 2, H - 72, 28);
    g.fillStyle(PALETTE.forestLight, 0.75);
    g.fillCircle(cx - 15, H - 95, 14);
    g.fillStyle(PALETTE.grassBright, 0.35);
    g.fillCircle(cx + 10, H - 88, 10);
    g.fillStyle(PALETTE.rafflesiaLight, 0.26);
    g.fillCircle(cx - 36, H - 90, 5);
    g.fillCircle(cx + 22, H - 98, 4);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.TREE_LARGE, W, H);
  }

  private static drawTreeFallen(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 140;
    const H = 56;

    g.fillStyle(PALETTE.woodDark);
    g.fillRoundedRect(10, 20, 120, 18, 6);
    g.fillStyle(0x2f1b10, 0.55);
    g.fillCircle(18, 29, 10);
    g.fillStyle(PALETTE.forestDark);
    g.fillCircle(18, 22, 22);
    g.fillCircle(30, 18, 16);
    g.fillStyle(PALETTE.forestMid);
    g.fillRect(10, 34, 120, 4);
    g.fillStyle(PALETTE.forestLight, 0.35);
    g.fillCircle(122, 20, 8);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.TREE_FALLEN, W, H);
  }

  private static drawGroundCrack(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 120;
    const H = 48;

    g.fillStyle(PALETTE.earth);
    g.fillRect(0, 0, W, H);
    g.fillStyle(0xc1121f, 0.15);
    g.fillEllipse(W / 2, H / 2, W - 8, H - 8);
    g.fillStyle(PALETTE.black, 0.85);
    g.fillRect(18, 6, 8, 32);
    g.fillRect(48, 10, 10, 28);
    g.fillRect(76, 4, 7, 34);
    g.fillRect(94, 14, 6, 20);
    g.lineStyle(3, 0x1a0a00, 0.9);
    g.lineBetween(22, 6, 52, 38);
    g.lineBetween(52, 10, 80, 36);
    g.lineBetween(80, 4, 98, 30);
    g.lineStyle(2, 0xff6b6b, 0.35);
    g.lineBetween(24, 8, 50, 34);
    g.lineBetween(54, 12, 78, 32);
    g.fillStyle(PALETTE.danger, 0.18);
    g.fillCircle(58, 24, 10);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.GROUND_CRACK, W, H);
  }

  private static drawRafflesia(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 52;
    const H = 52;
    const cx = W / 2;
    const cy = H / 2;

    g.fillStyle(0x4f7c3f, 0.45);
    g.fillEllipse(cx, cy + 10, 34, 18);
    g.fillStyle(PALETTE.rafflesia);
    g.fillCircle(cx, cy, 22);
    g.fillStyle(PALETTE.rafflesiaLight);
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      g.fillCircle(cx + Math.cos(angle) * 16, cy + Math.sin(angle) * 16, 8);
    }
    g.fillStyle(0xf3d9a0, 0.55);
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2 - 0.4;
      g.fillEllipse(cx + Math.cos(angle) * 10, cy + Math.sin(angle) * 10, 8, 4);
    }
    g.fillStyle(PALETTE.rafflesiaSpot);
    g.fillCircle(cx, cy, 8);
    g.fillStyle(PALETTE.forestDark);
    g.fillCircle(cx - 4, cy - 2, 2);
    g.fillCircle(cx + 4, cy - 2, 2);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.RAFFLESIA, W, H);
  }

  private static drawKeris(
    scene: Phaser.Scene,
    key: string,
    bladeColor: number,
    handleColor: number,
  ): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 36;
    const H = 72;
    const cx = W / 2;

    g.fillStyle(handleColor);
    g.fillRoundedRect(cx - 5, H - 28, 10, 24, 3);
    g.fillStyle(PALETTE.woodDark, 0.8);
    g.fillRoundedRect(cx - 3, H - 24, 6, 18, 2);
    g.fillStyle(PALETTE.gold);
    g.fillRect(cx - 7, H - 32, 14, 6);
    g.fillStyle(0xffd166, 0.2);
    g.fillCircle(cx, H / 2 - 4, 18);
    g.fillStyle(bladeColor);
    g.fillTriangle(cx, 8, cx - 8, H - 28, cx + 8, H - 28);
    g.fillStyle(PALETTE.stoneLight, 0.24);
    g.fillTriangle(cx, 14, cx - 4, H - 32, cx + 4, H - 32);
    g.lineStyle(1, PALETTE.white, 0.5);
    g.lineBetween(cx, 12, cx, H - 30);
    g.lineStyle(2, PALETTE.gold, 0.6);
    g.lineBetween(cx - 4, H - 30, cx + 4, 14);

    AssetGenerator.commit(scene, g, key, W, H);
  }

  private static drawHouseBubungan(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 200;
    const H = 170;
    const baseY = H - 10;

    g.fillStyle(PALETTE.woodDark);
    for (let i = 0; i < 6; i++) {
      g.fillRect(30 + i * 26, baseY - 58, 8, 58);
    }

    g.fillStyle(PALETTE.woodMid);
    g.fillRect(24, baseY - 66, 152, 10);
    g.fillStyle(PALETTE.woodLight);
    g.fillRect(28, baseY - 64, 144, 6);

    g.fillStyle(PALETTE.clothRed);
    g.fillTriangle(20, baseY - 66, 100, baseY - 138, 180, baseY - 66);
    g.fillStyle(PALETTE.woodDark);
    g.fillTriangle(30, baseY - 66, 100, baseY - 121, 170, baseY - 66);
    g.fillStyle(PALETTE.gold, 0.75);
    g.fillRect(82, baseY - 114, 36, 5);
    g.fillTriangle(100, baseY - 132, 92, baseY - 118, 108, baseY - 118);

    g.fillStyle(PALETTE.woodLight);
    g.fillRect(70, baseY - 48, 28, 32);
    g.fillStyle(PALETTE.black);
    g.fillRect(78, baseY - 40, 12, 20);
    g.fillStyle(PALETTE.woodLight, 0.8);
    g.fillRect(108, baseY - 52, 34, 22);
    g.fillStyle(PALETTE.black, 0.7);
    g.fillRect(112, baseY - 48, 10, 14);
    g.fillRect(126, baseY - 48, 10, 14);
    g.fillStyle(PALETTE.gold, 0.55);
    g.fillRect(66, baseY - 76, 68, 4);

    g.fillStyle(PALETTE.woodMid, 0.6);
    g.fillRect(24, baseY - 8, 152, 8);
    g.fillStyle(PALETTE.earthLight, 0.7);
    g.fillRect(18, baseY - 2, 164, 4);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.HOUSE_BUBUNGAN, W, H);
  }

  private static drawSignBoard(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 80;
    const H = 100;

    g.fillStyle(PALETTE.woodDark);
    g.fillRoundedRect(36, 50, 8, 48, 2);
    g.fillStyle(PALETTE.woodLight);
    g.fillRoundedRect(8, 8, 64, 48, 6);
    g.fillStyle(PALETTE.woodMid);
    g.fillRect(8, 8, 64, 6);
    g.fillStyle(PALETTE.gold);
    g.fillCircle(40, 20, 6);
    g.fillStyle(PALETTE.forestDark);
    g.fillRect(18, 30, 44, 3);
    g.fillRect(18, 38, 36, 3);
    g.fillStyle(PALETTE.rafflesiaLight, 0.8);
    g.fillCircle(58, 18, 4);
    g.lineStyle(1, PALETTE.woodDark, 0.5);
    g.strokeRoundedRect(10, 10, 60, 44, 6);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.SIGN_BOARD, W, H);
  }

  private static drawPathRiver(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 160;
    const H = 80;

    g.fillStyle(0x7fb77e);
    g.fillRect(0, 0, W, H);
    g.fillStyle(PALETTE.earthLight, 0.7);
    g.fillEllipse(W / 2, H / 2 + 18, 132, 50);
    g.fillStyle(PALETTE.water);
    g.fillEllipse(W / 2, H / 2 + 10, 120, 40);
    g.fillStyle(PALETTE.waterDeep, 0.26);
    g.fillEllipse(W / 2 + 10, H / 2 + 12, 92, 22);
    g.fillStyle(PALETTE.water, 0.6);
    g.fillEllipse(W / 2 - 20, H / 2 + 8, 30, 12);
    g.fillEllipse(W / 2 + 25, H / 2 + 14, 24, 10);
    g.fillStyle(0xc1121f, 0.25);
    g.fillTriangle(18, 10, 36, 10, 27, 28);
    g.fillStyle(0xffffff, 0.85);
    g.fillRect(24, 10, 6, 10);
    g.fillStyle(PALETTE.forestLight, 0.5);
    g.fillCircle(20, 15, 12);
    g.fillCircle(W - 25, 20, 10);
    g.fillStyle(PALETTE.woodDark, 0.75);
    g.fillRect(102, 16, 18, 4);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.PATH_RIVER, W, H);
  }

  private static drawPathHill(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 160;
    const H = 80;

    g.fillStyle(PALETTE.skyLight);
    g.fillRect(0, 0, W, 40);
    g.fillStyle(PALETTE.grass);
    g.fillRect(0, 30, W, H - 30);
    g.fillStyle(PALETTE.forestLight, 0.4);
    g.fillCircle(40, 55, 18);
    g.fillCircle(100, 60, 14);
    g.fillStyle(PALETTE.earth);
    g.fillTriangle(0, 80, 60, 35, 120, 80);
    g.fillStyle(PALETTE.grass);
    g.fillTriangle(60, 35, 120, 80, 160, 80);
    g.fillStyle(PALETTE.earthLight, 0.45);
    g.fillEllipse(62, 63, 74, 16);
    g.fillStyle(PALETTE.gold, 0.9);
    g.fillTriangle(126, 14, 136, 34, 146, 14);
    g.fillStyle(PALETTE.forestMid, 0.9);
    g.fillRect(133, 34, 6, 12);
    g.fillStyle(PALETTE.forestDark, 0.55);
    g.fillRect(24, 44, 10, 16);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.PATH_HILL, W, H);
  }

  /** Ikon titik kumpul aman — tenda darurat + tiang bendera + warga, latar transparan. */
  private static drawSafeGathering(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 100;
    const H = 100;

    // Bayangan tanah
    g.fillStyle(PALETTE.black, 0.22);
    g.fillEllipse(52, 84, 38, 9);

    // Tenda darurat
    g.fillStyle(PALETTE.white, 1);
    g.fillTriangle(28, 36, 8, 78, 48, 78);
    g.fillStyle(PALETTE.stone, 0.28);
    g.fillTriangle(28, 36, 28, 78, 48, 78);
    g.fillStyle(PALETTE.clothRed, 0.7);
    g.fillTriangle(28, 36, 20, 50, 36, 50);
    g.fillStyle(PALETTE.soilDark, 0.85);
    g.fillRoundedRect(23, 60, 10, 18, 3);

    // Tiang bendera
    g.fillStyle(PALETTE.woodDark, 1);
    g.fillRect(62, 12, 4, 68);
    g.fillStyle(PALETTE.gold, 1);
    g.fillCircle(64, 9, 3);
    g.fillStyle(PALETTE.clothRed, 1);
    g.fillTriangle(66, 14, 92, 21, 66, 30);
    g.fillStyle(PALETTE.gold, 0.65);
    g.fillTriangle(66, 16, 82, 21, 66, 26);

    // Warga berkumpul
    const person = (px: number, py: number, cloth: number, scale = 1) => {
      const bw = 9 * scale;
      const bh = 13 * scale;
      g.fillStyle(PALETTE.black, 0.18);
      g.fillEllipse(px, py + 2, 6 * scale, 2 * scale);
      g.fillStyle(cloth, 1);
      g.fillRoundedRect(px - bw / 2, py - bh, bw, bh, 3);
      g.fillStyle(PALETTE.skin, 1);
      g.fillCircle(px, py - bh - 4.5 * scale + 2, 4.5 * scale);
    };
    person(45, 80, PALETTE.clothWarm, 0.9);
    person(55, 84, PALETTE.clothGreen, 1);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.SAFE_GATHERING, W, H);
  }

  // ─── Backgrounds ────────────────────────────────────────────

  private static generateBackgrounds(scene: Phaser.Scene, tick: () => void): void {
    AssetGenerator.drawMenuBackground(scene);
    tick();
    AssetGenerator.drawZone1Background(scene);
    tick();
    AssetGenerator.drawZone2Background(scene);
    tick();
    AssetGenerator.drawZone3Background(scene);
    tick();
    AssetGenerator.drawZone4Background(scene);
    tick();
    AssetGenerator.drawZone5Background(scene);
    tick();
    AssetGenerator.drawEndingBackground(scene);
    tick();
  }

  private static drawCloudCluster(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    scale = 1,
    alpha = 0.3,
  ): void {
    g.fillStyle(PALETTE.white, alpha);
    g.fillEllipse(x, y, 150 * scale, 42 * scale);
    g.fillEllipse(x - 42 * scale, y + 10 * scale, 84 * scale, 34 * scale);
    g.fillEllipse(x + 46 * scale, y + 8 * scale, 78 * scale, 30 * scale);
  }

  private static drawMountainBand(
    g: Phaser.GameObjects.Graphics,
    points: Array<{ x: number; y: number }>,
    color: number,
    alpha: number,
    baseY: number,
  ): void {
    g.fillStyle(color, alpha);
    g.beginPath();
    g.moveTo(0, baseY);
    points.forEach((p) => g.lineTo(p.x, p.y));
    g.lineTo(1280, baseY);
    g.closePath();
    g.fillPath();
  }

  private static drawMenuBackground(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0xdff5ff, 0xdff5ff, 0x9fd7f5, 0x4f81b4);
    g.fillRect(0, 0, W, H);

    g.fillStyle(0xffffff, 0.18);
    g.fillEllipse(220, 120, 240, 70);
    g.fillEllipse(860, 130, 300, 84);

    g.fillStyle(0x567c5a, 0.45);
    g.fillTriangle(0, 520, 240, 250, 560, 520);
    g.fillTriangle(320, 530, 650, 270, 980, 530);
    g.fillTriangle(760, 540, 1040, 310, 1280, 540);

    g.fillStyle(0x35543a, 0.32);
    g.fillTriangle(0, 560, 170, 340, 360, 560);
    g.fillTriangle(250, 560, 490, 340, 760, 560);
    g.fillTriangle(620, 570, 910, 350, 1210, 570);

    g.fillStyle(PALETTE.grassBright);
    g.fillRect(0, 480, W, 240);
    g.fillStyle(0x7ca95a, 0.9);
    g.fillEllipse(230, 520, 560, 80);
    g.fillEllipse(1060, 540, 460, 70);

    g.fillStyle(PALETTE.forestMid, 0.65);
    for (let i = 0; i < 12; i++) {
      const x = 60 + i * 100;
      g.fillTriangle(x, 500, x + 30, 340, x + 60, 500);
    }

    g.fillStyle(PALETTE.woodDark, 0.3);
    g.fillRect(960, 358, 138, 102);
    g.fillTriangle(936, 358, 1030, 278, 1124, 358);
    g.fillStyle(PALETTE.woodLight, 0.22);
    g.fillRect(982, 390, 28, 50);
    g.fillRect(1040, 388, 34, 26);
    g.fillStyle(PALETTE.gold, 0.24);
    g.fillRect(995, 336, 72, 5);

    g.fillStyle(PALETTE.rafflesia, 0.85);
    g.fillCircle(180, 560, 18);
    g.fillCircle(200, 548, 12);
    g.fillStyle(PALETTE.rafflesiaLight, 0.7);
    g.fillCircle(1050, 570, 16);
    g.fillCircle(1070, 558, 10);
    g.fillStyle(0xffffff, 0.26);
    g.fillEllipse(150, 110, 170, 56);
    g.fillEllipse(1120, 120, 140, 48);
    g.fillStyle(PALETTE.gold, 0.2);
    g.fillCircle(980, 80, 42);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_MENU, W, H);
  }

  private static drawZone1Background(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0xe8f6ff, 0xe8f6ff, 0xc4e0ef, 0x7aa4c3, 1);
    g.fillRect(0, 0, W, H);

    AssetGenerator.drawCloudCluster(g, 250, 116, 0.9, 0.42);
    AssetGenerator.drawCloudCluster(g, 970, 96, 1.05, 0.36);
    g.fillStyle(PALETTE.gold, 0.12);
    g.fillCircle(1010, 92, 34);

    AssetGenerator.drawMountainBand(g, [
      { x: 80, y: 290 },
      { x: 250, y: 210 },
      { x: 440, y: 282 },
      { x: 700, y: 225 },
      { x: 910, y: 286 },
      { x: 1180, y: 198 },
    ], 0x8db2bd, 0.36, 398);
    AssetGenerator.drawMountainBand(g, [
      { x: 0, y: 392 },
      { x: 170, y: 254 },
      { x: 410, y: 390 },
      { x: 650, y: 232 },
      { x: 910, y: 384 },
      { x: 1185, y: 258 },
    ], 0x5d8a78, 0.28, 430);
    AssetGenerator.drawMountainBand(g, [
      { x: 120, y: 338 },
      { x: 320, y: 246 },
      { x: 570, y: 348 },
      { x: 830, y: 258 },
      { x: 1100, y: 342 },
    ], 0x406b59, 0.34, 454);

    g.fillStyle(PALETTE.mist, 0.18);
    g.fillRect(0, 352, W, 40);
    g.fillStyle(0x7db6a0, 0.18);
    g.fillRect(0, 390, W, 28);
    g.fillStyle(0x4d8f64, 0.4);
    g.fillEllipse(230, 430, 340, 86);
    g.fillEllipse(710, 418, 540, 108);
    g.fillEllipse(1060, 432, 370, 90);

    g.fillStyle(PALETTE.grassBright);
    g.fillRect(0, 430, W, 290);
    g.fillStyle(0x85c88a, 0.92);
    g.fillEllipse(250, 520, 420, 110);
    g.fillEllipse(820, 525, 760, 130);
    g.fillStyle(0x6ea56f, 0.55);
    g.fillEllipse(1000, 555, 520, 96);
    g.fillStyle(PALETTE.earthLight, 0.18);
    g.fillEllipse(520, 565, 170, 34);
    g.fillEllipse(820, 560, 140, 30);

    g.fillStyle(PALETTE.forestDark, 0.26);
    for (let i = 0; i < 12; i++) {
      const x = 20 + i * 108;
      g.fillTriangle(x, 432, x + 34, 338 - (i % 3) * 10, x + 68, 432);
    }

    g.fillStyle(PALETTE.woodDark, 0.28);
    g.fillRect(900, 336, 118, 88);
    g.fillTriangle(878, 336, 958, 276, 1038, 336);
    g.fillStyle(PALETTE.woodLight, 0.15);
    g.fillRect(924, 368, 28, 54);
    g.fillRect(980, 368, 34, 24);
    g.fillStyle(PALETTE.gold, 0.18);
    g.fillRect(930, 332, 70, 4);
    g.fillStyle(PALETTE.rafflesiaLight, 0.16);
    g.fillCircle(166, 530, 22);
    g.fillCircle(1120, 540, 18);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_ZONE1, W, H);
  }

  private static drawZone2Background(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0xe6fbf1, 0xe6fbf1, 0xa7d8bf, 0x567a63);
    g.fillRect(0, 0, W, H);

    AssetGenerator.drawCloudCluster(g, 260, 100, 1.05, 0.2);
    AssetGenerator.drawCloudCluster(g, 920, 130, 0.9, 0.16);
    AssetGenerator.drawMountainBand(g, [
      { x: 80, y: 318 },
      { x: 260, y: 230 },
      { x: 480, y: 300 },
      { x: 760, y: 196 },
      { x: 980, y: 270 },
      { x: 1180, y: 206 },
    ], 0x607f71, 0.25, 410);
    AssetGenerator.drawMountainBand(g, [
      { x: 0, y: 372 },
      { x: 220, y: 268 },
      { x: 420, y: 348 },
      { x: 700, y: 236 },
      { x: 940, y: 344 },
      { x: 1180, y: 256 },
    ], 0x3b5d4c, 0.3, 454);

    g.fillStyle(0xdaf6ec, 0.08);
    g.fillTriangle(170, 0, 260, 0, 215, 320);
    g.fillTriangle(740, 0, 830, 0, 785, 340);
    g.fillStyle(0x2f6b48, 0.42);
    for (let i = 0; i < 14; i++) {
      const x = 40 + i * 90;
      g.fillTriangle(x, 470, x + 28, 320 - (i % 3) * 15, x + 56, 470);
    }

    g.fillStyle(0x80bf79, 0.92);
    g.fillRect(0, 470, W, 250);
    g.fillStyle(0x5b8b58, 0.76);
    g.fillEllipse(240, 560, 500, 120);
    g.fillEllipse(940, 590, 650, 130);
    g.fillStyle(0x8b6b49, 0.3);
    g.fillEllipse(630, 570, 820, 114);
    g.fillStyle(0xffffff, 0.07);
    g.fillRect(0, 378, W, 34);
    g.fillStyle(PALETTE.danger, 0.04);
    g.fillEllipse(880, 282, 180, 84);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_ZONE2, W, H);
  }

  private static drawZone3Background(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0xfff1db, 0xfff1db, 0xe2c8a4, 0x8c6a4f);
    g.fillRect(0, 0, W, H);

    AssetGenerator.drawCloudCluster(g, 240, 110, 1.05, 0.22);
    AssetGenerator.drawCloudCluster(g, 980, 96, 0.88, 0.18);
    AssetGenerator.drawMountainBand(g, [
      { x: 100, y: 320 },
      { x: 260, y: 230 },
      { x: 460, y: 300 },
      { x: 720, y: 210 },
      { x: 980, y: 280 },
      { x: 1180, y: 230 },
    ], 0x8b7b63, 0.22, 430);

    g.fillStyle(0x9cde8e, 0.82);
    g.fillRect(0, 450, W, 270);
    g.fillStyle(0xc4a882, 0.58);
    g.fillEllipse(380, 570, 560, 120);
    g.fillEllipse(910, 585, 700, 116);
    g.fillStyle(0x7f5a3a, 0.26);
    g.fillRect(760, 360, 210, 110);
    g.fillTriangle(730, 360, 865, 270, 1000, 360);
    g.fillStyle(PALETTE.woodLight, 0.12);
    g.fillRect(795, 398, 26, 52);
    g.fillRect(875, 392, 38, 28);
    g.fillStyle(0xffffff, 0.1);
    g.fillRect(0, 378, W, 36);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_ZONE3, W, H);
  }

  private static drawZone4Background(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0xe2f7ff, 0xe2f7ff, 0x9ec5d8, 0x5f7b8f);
    g.fillRect(0, 0, W, H);

    AssetGenerator.drawCloudCluster(g, 300, 96, 1, 0.2);
    AssetGenerator.drawCloudCluster(g, 980, 118, 0.9, 0.16);
    AssetGenerator.drawMountainBand(g, [
      { x: 120, y: 300 },
      { x: 280, y: 210 },
      { x: 500, y: 290 },
      { x: 720, y: 190 },
      { x: 960, y: 260 },
      { x: 1160, y: 220 },
    ], 0x5b7a6a, 0.25, 430);

    g.fillStyle(0x8bcf8b, 0.9);
    g.fillRect(0, 430, W, 290);
    g.fillStyle(0x74b37e, 0.88);
    g.fillEllipse(840, 470, 600, 140);
    g.fillEllipse(210, 560, 420, 90);
    g.fillStyle(PALETTE.waterDeep, 0.42);
    g.fillEllipse(300, 645, 320, 74);
    g.fillRect(180, 610, 340, 60);
    g.fillStyle(PALETTE.water, 0.24);
    g.fillEllipse(280, 632, 220, 28);
    g.fillEllipse(400, 650, 160, 20);
    g.fillStyle(PALETTE.danger, 0.08);
    g.fillRect(0, 0, 370, H);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_ZONE4, W, H);
  }

  private static drawZone5Background(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0xf7f8de, 0xf7f8de, 0xd1d7a2, 0x7f8960);
    g.fillRect(0, 0, W, H);

    AssetGenerator.drawCloudCluster(g, 240, 90, 0.95, 0.22);
    AssetGenerator.drawCloudCluster(g, 920, 104, 1.05, 0.18);
    AssetGenerator.drawMountainBand(g, [
      { x: 80, y: 310 },
      { x: 240, y: 240 },
      { x: 460, y: 300 },
      { x: 720, y: 220 },
      { x: 1020, y: 310 },
    ], 0x76826a, 0.22, 425);

    g.fillStyle(0x9ccf88, 0.92);
    g.fillRect(0, 440, W, 280);
    g.fillStyle(0x72a05e, 0.85);
    g.fillEllipse(300, 560, 560, 120);
    g.fillEllipse(930, 545, 660, 120);
    g.fillStyle(0xf2c14e, 0.12);
    g.fillCircle(1010, 84, 44);
    g.fillStyle(0xffffff, 0.1);
    g.fillRect(0, 385, W, 34);
    g.fillStyle(PALETTE.gold, 0.08);
    g.fillEllipse(860, 500, 220, 130);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_ZONE5, W, H);
  }

  private static drawEndingBackground(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 1280;
    const H = 720;

    g.fillGradientStyle(0x10283c, 0x10283c, 0x234e6b, 0x91c89a);
    g.fillRect(0, 0, W, H);

    g.fillStyle(PALETTE.gold, 0.18);
    g.fillCircle(640, 120, 86);
    g.fillStyle(PALETTE.gold, 0.08);
    g.fillCircle(640, 120, 150);

    AssetGenerator.drawMountainBand(g, [
      { x: 90, y: 360 },
      { x: 260, y: 270 },
      { x: 460, y: 340 },
      { x: 670, y: 220 },
      { x: 900, y: 320 },
      { x: 1130, y: 250 },
    ], 0x284a40, 0.4, 480);

    g.fillStyle(0x40724d, 0.88);
    g.fillRect(0, 470, W, 250);
    g.fillStyle(0x5e965c, 0.85);
    g.fillEllipse(270, 550, 580, 124);
    g.fillEllipse(1020, 548, 620, 120);
    g.fillStyle(PALETTE.white, 0.08);
    g.fillRect(0, 390, W, 36);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.BG_ENDING, W, H);
  }

  // ─── UI ───────────────────────────────────────────────────

  private static generateUI(scene: Phaser.Scene, tick: () => void): void {
    AssetGenerator.drawUIButton(scene);
    tick();
    AssetGenerator.drawUIPanel(scene, TEXTURE_KEYS.UI_DIALOGUE, 1240, 170, PALETTE.forestDark, 0.94);
    tick();
    AssetGenerator.drawUIPanel(scene, TEXTURE_KEYS.UI_QUEST, 310, 130, PALETTE.forestDark, 0.92);
    tick();
    AssetGenerator.drawIconRafflesia(scene);
    tick();
  }

  private static drawUIButton(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 280;
    const H = 52;

    g.fillStyle(PALETTE.woodMid, 1);
    g.fillRoundedRect(0, 0, W, H, 18);
    g.fillStyle(PALETTE.woodLight, 0.88);
    g.fillRoundedRect(4, 4, W - 8, H - 8, 15);
    g.fillStyle(0x4f8d67, 1);
    g.fillRoundedRect(8, 7, W - 16, H - 14, 13);
    g.fillStyle(0x7fd1a1, 0.26);
    g.fillRoundedRect(14, 11, W - 28, 10, 6);
    g.fillStyle(PALETTE.white, 0.14);
    g.fillRoundedRect(16, H / 2, W - 32, H / 2 - 12, 8);
    g.fillStyle(PALETTE.black, 0.1);
    g.fillRect(22, H - 12, W - 44, 2);
    g.fillStyle(PALETTE.gold, 0.1);
    g.fillCircle(24, H / 2, 6);
    g.fillCircle(W - 24, H / 2, 6);
    g.lineStyle(2, PALETTE.gold, 0.26);
    g.strokeRoundedRect(1.5, 1.5, W - 3, H - 3, 17);
    g.lineStyle(1, PALETTE.white, 0.16);
    g.strokeRoundedRect(8, 7, W - 16, H - 14, 13);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.UI_BUTTON, W, H);
  }

  private static drawUIPanel(
    scene: Phaser.Scene,
    key: string,
    w: number,
    h: number,
    color: number,
    alpha: number,
  ): void {
    const g = AssetGenerator.makeGraphics(scene);
    g.fillStyle(0x0d1621, alpha);
    g.fillRoundedRect(0, 0, w, h, 22);
    g.fillStyle(color, 0.3);
    g.fillRoundedRect(8, 8, w - 16, Math.max(42, h * 0.18), 16);
    g.fillStyle(PALETTE.white, 0.04);
    g.fillRoundedRect(10, 10, w - 20, h - 20, 16);
    g.fillStyle(PALETTE.gold, 0.1);
    g.fillRect(24, 18, w - 48, 4);
    g.fillRect(24, h - 22, w - 48, 3);
    g.lineStyle(2, PALETTE.gold, 0.56);
    g.strokeRoundedRect(1, 1, w - 2, h - 2, 21);
    g.lineStyle(1, PALETTE.white, 0.08);
    g.strokeRoundedRect(8, 8, w - 16, h - 16, 16);
    g.fillStyle(PALETTE.rafflesiaLight, 0.22);
    g.fillCircle(26, 24, 5);
    g.fillCircle(w - 26, 24, 5);
    AssetGenerator.commit(scene, g, key, w, h);
  }

  private static drawIconRafflesia(scene: Phaser.Scene): void {
    const g = AssetGenerator.makeGraphics(scene);
    const W = 28;
    const H = 28;
    const cx = W / 2;
    const cy = H / 2;

    g.fillStyle(PALETTE.rafflesia);
    g.fillCircle(cx, cy, 12);
    g.fillStyle(PALETTE.rafflesiaLight);
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      g.fillEllipse(cx + Math.cos(angle) * 7, cy + Math.sin(angle) * 7, 6, 4);
    }
    g.fillStyle(PALETTE.rafflesiaSpot);
    g.fillCircle(cx, cy, 5);
    g.fillStyle(PALETTE.forestDark, 0.65);
    g.fillCircle(cx, cy, 2);

    AssetGenerator.commit(scene, g, TEXTURE_KEYS.ICON_RAFFLESIA, W, H);
  }
}
